import type { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCheckbox } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import Accordion from 'components/Accordion/Accordion';
import { QuickFilterContext } from 'components/Filter/QuickFilter';
import { globalStackStylesHeight100PaddingSmall, globalStackTokensGapSmall } from 'globalStyles';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import { useContext, useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  criteriaFilterGroupKey,
  criteriaFilterResults,
  getCriteriaFilterEntryText,
  getCriteriaFilterValue,
  isCriteriaColumnKey,
} from './useCriteriaColumns';

//
// The criteria columns a register shows, for its filter panel (PLAN-075 Task 30): the panel is rendered by the
// register's parent scene, the columns are known in the register. One entry per register (its quick-filter context).
//
interface ICriteriaFilterColumn {
  lineageKey: string;
  name: string;
}

const noColumns: ICriteriaFilterColumn[] = [];
const shownColumnsByContext = new Map<QuickFilterContext, ICriteriaFilterColumn[]>();
const listeners = new Set<() => void>();

const publishShownColumns = (context: QuickFilterContext, columns: ICriteriaFilterColumn[] | undefined) => {
  if (columns) {
    shownColumnsByContext.set(context, columns);
  } else {
    shownColumnsByContext.delete(context);
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** The register tells its filter panel which criteria columns it shows. */
export const usePublishCriteriaFilterColumns = (context: QuickFilterContext, shownColumns: IColumn[]) => {
  const columns = shownColumns
    .filter((column) => isCriteriaColumnKey(column.key))
    .map((column) => ({ lineageKey: (column.data?.lineageKey as string) ?? '', name: column.name }));
  const columnsKey = columns.map((column) => `${column.lineageKey}\u0001${column.name}`).join('\u0002');

  useEffect(() => {
    publishShownColumns(context, columns);

    return () => publishShownColumns(context, undefined);
    // the columns as one string: the column arrays change identity on every render of a register
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, columnsKey]);
};

interface ICriteriaFilterGroupProps {
  context: QuickFilterContext;
  selectedFilter: string[];
  onChange: (filter: string) => void;
}

/** The group "Audit criteria" of a register's filter panel: per shown criterion column its Pass, Fail and Not assessed. */
export const CriteriaFilterGroup = (props: ICriteriaFilterGroupProps) => {
  const { t } = useTranslation(['criteria']);
  const appContext = useContext(AppContext);
  const columns = useSyncExternalStore(subscribe, () => shownColumnsByContext.get(props.context) ?? noColumns);

  if (!isAuditCriteriaPreviewEnabled(appContext) || columns.length === 0) return null;

  return (
    <Accordion
      title={t('criteria:Columns.Filter.Group', {
        count: props.selectedFilter.filter((filter) => filter.startsWith(criteriaFilterGroupKey)).length,
      })}
    >
      <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
        {columns.flatMap((column) =>
          criteriaFilterResults.map((result) => {
            const value = getCriteriaFilterValue(column.lineageKey, result);

            return (
              <IPCheckbox
                key={value}
                label={getCriteriaFilterEntryText(column.name, result, t)}
                checked={props.selectedFilter.includes(value)}
                onChange={() => props.onChange(value)}
              />
            );
          }),
        )}
      </IPStack>
    </Accordion>
  );
};
